import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTrackGeneratedShareUrl } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { ShortUrlObjectType } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type AssetShareLinkSlotId } from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { getAssetShareLinksListKey } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { buildShareUrlGeneratedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlGeneratedAnalyticsParams';
import { resolveShareUrlGeneratedVersion } from '~/utils/shortUrl/getShareUrlGeneratedVersion';

type GetShortIdForAssetMutationParams = Parameters<typeof ShortUrl.getShortIdForAsset>[0] & {
  versionSlot?: AssetShareLinkSlotId;
};

export const useGetShortIdForAsset = () => {
  const queryClient = useQueryClient();
  const { trackGeneratedShareUrl } = useTrackGeneratedShareUrl();

  return useMutation({
    mutationFn: ({ versionSlot: _versionSlot, ...params }: GetShortIdForAssetMutationParams) =>
      ShortUrl.getShortIdForAsset(params),
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({
        queryKey: getAssetShareLinksListKey({ workspaceId: params.workspaceId, assetId: params.id }),
      });

      trackGeneratedShareUrl(
        buildShareUrlGeneratedAnalyticsParams({
          shareId: data.id,
          shortUrl: data,
          objectType: ShortUrlObjectType.asset,
          version: resolveShareUrlGeneratedVersion({
            versionSlot: params.versionSlot,
            objectType: 'asset',
          }),
        }),
      );
    },
    onError: (error, params) => {
      throw reportErrorToBugsnag({
        error,
        context: 'Failed to get short id for asset',
        metadata: { data: { params } },
      });
    },
  });
};
